// 1. Framework / library imports
import { memo, useCallback, useEffect, useState } from "react";

// 2. Helpers & Hooks
import { classNames, useTranslate } from "helpers";
import {
  useHandleDropdownChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";
import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";

// 3. API & Types
import {
  getTireManufacturersAPI,
} from "services/api/calculations/tire/api";
import {
  createFleetTireAPI,
  getTireRadiusAPI,
  getTireHeightsAPI,
  getTireTypesAPI,
  getTireWidthsAPI,
  updateFleetTireAPI,
} from "services/api/fleet/tires/api";

import {
  FleetTireManufacturer,
  FleetTireAddFormValues,
  TireType,
  TireWidth,
  TireHeight,
  TireRadius,
} from "services/api/fleet/tires/types";

import { initialFleetTireAddFormValues } from "services/api/fleet/tires/consts";
import { initialTireManufacturerDataWithPagination } from "../TireDetailPage/components/TireEditCard/consts";

import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

// 4. UI Components
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { TextField } from "ui/components/TextField/TextField";
import { Toggle } from "ui/components/Toggle";


// 5. Local Types & Constants
import { TireAddModalProps } from "./types";
import { TIRE_ADD_FORM_SCHEMA } from "./validation";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";
import TireAddQuantityModal from "./TireAddQuantityModal";
import TireManufacturerDropdown from "ui/components/Dropdown/TireManufacturerDropdown/TireManufacturerDropdown";
import TireTypeDropdown from "ui/components/Dropdown/TireTypeDropdown/TireTypeDropdown";
import TireDimensionDropdowns from "ui/components/Dropdown/TireDimensionDropdowns/TireDimensionDropdowns";



const TireAddModal: React.FC<TireAddModalProps> = (props) => {
  const { show, vehicleId, tireId, defaultAxles, fromTireSet, maxQuantity, onSuccess, onClose, modalSize = "medium" } = props;

  const { createDialog } = useDialog();

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showQuantityModal, setShowQuantityModal] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<FleetTireAddFormValues>(
    initialFleetTireAddFormValues
  );

  const [tireTypes, setTireTypes] = useState<DataWithPagination<TireType>>(
    initialDataWithPagination
  );
  const [tireWidths, setTireWidths] = useState<TireWidth[]>([]);
  const [tireHeights, setTireHeights] = useState<TireHeight[]>([]);
  const [tireRadius, setTireRadius] = useState<TireRadius[]>([]);

  const [tireManufacturers, setTireManufacturers] = useState<
    DataWithPagination<FleetTireManufacturer>
  >(initialTireManufacturerDataWithPagination);

  useEffect(() => {
    fetchTireTypes(tireTypes.pagination);
    fetchTireWidth();
    fetchTireHeight();
    fetchTireradius();
    fetchTireManufacturers();
  }, []);

  useEffect(() => {
    if (vehicleId) {
      setFormValues(prev => ({
        ...prev,
        vehicleId: vehicleId,
      }));
    }
  }, [vehicleId]);

  useEffect(() => {
    if (!defaultAxles?.length || tireId) return;
    const firstAxle = defaultAxles[0];
    setFormValues(prev => ({
      ...prev,
      width: prev.width ?? firstAxle.width,
      height: prev.height ?? firstAxle.ratio,
      radius: prev.radius ?? firstAxle.diameter,
    }));
  }, [defaultAxles]);

  const fetchTireTypes = async (pagination: Pagination) => {
    setIsLoading(true);
    try {
      const results = await getTireTypesAPI(pagination);

      setTireTypes(results);
    } catch {
      setTireTypes(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const fetchTireWidth = async () => {
    setIsLoading(true);
    try {
      const results = await getTireWidthsAPI();

      setTireWidths(results);
    } catch {
      setTireWidths([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const fetchTireHeight = async () => {
    setIsLoading(true);
    try {
      const results = await getTireHeightsAPI();

      setTireHeights(results);
    } catch {
      setTireHeights([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const fetchTireradius = async () => {
    setIsLoading(true);
    try {
      const results = await getTireRadiusAPI();

      setTireRadius(results);
    } catch {
      setTireRadius([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const fetchTireManufacturers = async () => {
    setIsLoading(true);

    let result;
    try {
      result = await getTireManufacturersAPI(tireManufacturers.pagination);
    } catch {
      setIsLoading(false);
      return;
    }

    setTireManufacturers(result);

    setIsLoading(false);

    setErrors({});
  };

  const handleAddClick = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(formValues, TIRE_ADD_FORM_SCHEMA, setErrors);

    if (!isValid) {
      return;
    }

    setShowQuantityModal(true);
    setIsLoading(false);
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(formValues, TIRE_ADD_FORM_SCHEMA, setErrors);

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let results;
      if (!!tireId) {
        results = await updateFleetTireAPI(tireId, formValues, t);
      } else {
        results = await createFleetTireAPI(formValues, t);
      }

      if (results && onSuccess) {
        onSuccess(results);
        setShowQuantityModal(false);
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };


  const widthItems: DropdownOption[] = tireWidths.map((i) => {
    return {
      value: i.id,
      text: `${i.name}`,
    };
  });

  const heightItems: DropdownOption[] = tireHeights.map((i) => {
    return {
      value: i.id,
      text: `${i.name}`,
    };
  });

  const radiusItems: DropdownOption[] = tireRadius.map((i) => {
    return {
      value: i.id,
      text: `${i.name}`,
    };
  });

  const manufacturerItems: DropdownOption[] = tireManufacturers?.data.map(
    (i) => {
      return {
        value: i.id,
        text: i.name,
      };
    }
  );

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  const handleActiveChange = useCallback(
    (id: string, name: string, value: boolean) => {
      setFormValues({ ...formValues, [name]: value });
    },
    [setFormValues, formValues]
  );

  const handleCloseModal = () => {
    createDialog({
      onConfirm: onClose
    })
  }

  return (
    <>
      <Modal show={show} onHide={onClose}   position="centered" size="medium" >
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title={tireId ? t("admin.fleet.tire.edit.title") : t("admin.fleet.tire.add.title")}
          onHide={onClose}
        />
        <ModalBody overflow={false}>
          <div className="flex gap-4">
            <div className={classNames("w-full m-auto", modalSize === "full" ? "h-screen" : "")}>
              <div className="flex gap-3 h-full">
                <form noValidate className="text-left flex-1">
                  <div className="">
                    <div className="px-4 pt-4 pb-4">
                      <div>
                        <div className="mb-3">
                          <TireDimensionDropdowns
                            width={formValues.width}
                            height={formValues.height}
                            radius={formValues.radius}
                            onChange={(field, value) => setFormValues((prev) => ({ ...prev, [field]: value }))}
                            errors={{ width: errors.width, height: errors.height, radius: errors.radius }}
                          />
                        </div>
                        <div className="flex gap-4">
                          <div className="flex flex-1 mb-3">
                            <div className="flex-1">

                              <TireManufacturerDropdown
                                label={t("admin.fleet.tire.add.manufacturer")}
                                name="tireManufacturerId"
                                onChange={handleDropdownChange}
                                value={formValues.tireManufacturerId}
                                direction="down"
                                error={errors.tireManufacturerId}
                                required
                                width="full"
                              />
                            </div>
                          </div>
                          <div className="flex flex-1 mb-3">
                            <div className="flex-1">
                              <TireTypeDropdown
                                label={t("admin.fleet.tire.add.tiretype")}
                                name="tireTypeId"
                                onChange={handleDropdownChange}
                                value={formValues.tireTypeId}
                                direction="down"
                                error={errors.tireTypeId}
                                required
                                width="full"
                              />
                            </div>
                          </div>
                          <div className="flex flex-1 mb-3">
                            <div className="flex-1">
                              <div className="flex gap-3 px-0 pt-10 ">
                                <div>
                                  <p className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
                                    {t("admin.fleet.tire.add.rof")}
                                  </p>
                                </div>
                                <div>
                                  <Toggle
                                    id="rof"
                                    name="rof"
                                    value={formValues.rof}
                                    onChange={handleActiveChange}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-4">
                          <div className="flex flex-1 mb-3">
                            <div className="flex-1">
                              <TextField
                                type="number"
                                name="profileDepth"
                                label={t("admin.fleet.tire.add.profiledepth")}
                                placeholder={t(
                                  "admin.fleet.tire.add.profiledepth.placeholder"
                                )}
                                onChange={handleTextFieldChange}
                                error={errors.profileDepth}
                                value={formValues.profileDepth}
                                required
                                groupAppend="cm"

                              ></TextField>
                            </div>
                          </div>
                          <div className="flex flex-1 mb-3">
                            <div className="flex-1">
                              <TextField
                                type="number"
                                name="dot"
                                label={t("admin.fleet.tire.add.dot")}
                                placeholder={t(
                                  "admin.fleet.tire.add.dot.placeholder"
                                )}
                                onChange={handleTextFieldChange}
                                error={errors.dot}
                                value={formValues.dot}
                                required
                                groupAppend="MM/YY"

                              ></TextField>
                            </div>
                          </div>
                          <div className="flex flex-1 mb-3">
                            <div className="flex-1">

                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                  <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                    <Button
                      variant="outlined"
                      children={t("admin.close")}
                      onClick={handleCloseModal}
                    />
                    <Button
                      variant="primary"
                      children={t("admin.fleet.tire.add.button")}
                      onClick={handleAddClick}
                    />
                  </div>

                </form>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
      <TireAddQuantityModal
        show={showQuantityModal}
        quantity={formValues.quantity}
        fromTireSet={fromTireSet}
        maxQuantity={maxQuantity}
        errors={errors}
        onQuantityChange={handleDropdownChange}
        onSubmit={handleFormSubmit}
        onClose={() => setShowQuantityModal(false)}

      />
      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(TireAddModal);
